import { DiskPanel } from './DiskPanel'
import { useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router'
import { CircuitBoard, MonitorSmartphone } from 'lucide-react'
import { ApiError } from '@/api/client'
import { leases as leaseApi } from '@/api/endpoints'
import { Ago } from '@/components/Ago'
import { Err } from '@/components/Err'
import { ExpertLine } from '@/components/Expert'
import { useExpert } from '@/hooks/useExpert'
import { Facts, type FactRow } from '@/components/Facts'
import { ToneBadge } from '@/components/ToneBadge'
import { Crumb, Empty, PageHead, Section } from '@/components/layout/Page'
import { usePoll } from '@/hooks/usePoll'
import { useSession } from '@/hooks/useSession'
import { useWorldRefresh } from '@/hooks/useWorld'
import { useFallback, useRefetchOn } from '@/hooks/useEventStream'
import { UNREACHABLE_WORD, isUnreachable, leaseWord, leaseToneOf, mayChange, mayTag, requestText, vmWord, vmSpecText, vmToneOf } from '@/domain/lease'
import { ownerLabel } from '@/domain/owner'
import { DASH, exact, sizeMb } from '@/ui/format'
import type { Lease, Vm } from '@/api/types'
import type { World } from './model'
import { AppPanel } from './AppPanel'
import { GuestKeysLine } from './GuestKeysLine'
import { KeyRotateChip } from './KeyRotateChip'
import { ConsoleChip } from './ConsoleDialog'
import { isClosed, retainedOf } from '@/domain/lease'
import { RetentionActions } from './RetentionActions'
import { ChangeLine } from './user/ChangeLine'
import { AccessPanel, KeyDownload } from './vm/AccessPanel'
import { ACTION, ACTION_PLAIN, CHIP } from './vm/chips'
import { ChangeDialog } from './user/ChangeDialog'
import { PowerActions } from './vm/PowerActions'
import { VmStatusPanel } from './vm/VmStatusPanel'
import { TagEditChip, VmTags } from './vm/VmTags'

const LEASE_POLL_MS = 5000

// 한 VM 의 현재: 상태 · 접속 · 디스크 · 앱 · 임대 · GPU. 값이 없으면 대시다.
// 경로의 id 는 임대 id 다 — 임대 하나를 읽고 그 `vm`(산 게스트)을 그린다. 산 게스트가 없으면 `없는 VM` 이다.
// `상태`·`접속` 은 사용자 상세와 같은 칸을 쓴다 — 같은 VM 을 보는 화면이 둘일 이유가 없다.
// `접속` 절(명령 한 줄 · config · 키 · 플랫폼 밖의 키)은 소유자에게 늘 있고,
// 남의 VM 에서는 **고급을 켰을 때만** 있다(B-33) — 평소의 결정에 쓰이지 않는 것이라 기본 화면에 두지 않는다.
// 키를 받는 칩은 서버가 링크를 준 사람(소유자·최고 관리자·소유자가 속한 팀의 관리자)에게만 선다.
// 남의 VM 을 바꾸는 조작(전원 · 반납 · 키 재발급 · 변경 취소 · 디스크 · 앱 공개 · 태그 수정)은 `mayChange` 가 가른다 —
// 전원과 반납은 사용자 상세와 같은 `PowerActions` 이고 VM 목록의 일괄 조작과 같은 표(`POWER`)를 읽는다.
// 관리자는 자기 팀 사람의 VM 을 보고 콘솔과 키로 들어가기만 한다. 보존 기한의 파기·되살리기는 최고 관리자의 것이다.
// 관리자에게만 있는 것은 임대·GPU 절과 전문가 행이다. 최고 관리자에게는 `사양 수정` 칩이 하나 더 선다(B-43).

export default function VmView({ world }: { world: World }) {
  const { id = '' } = useParams()
  // 경로의 임대가 바뀌면 처음부터 다시 읽는다 — 앞 VM 의 읽기·오류·열린 창이 다음 VM 에 남지 않는다.
  return <Detail key={id} id={id} world={world} />
}

function Detail({ id, world }: { id: string; world: World }) {
  const me = useSession()
  const expert = useExpert()
  const refresh = useWorldRefresh()
  const lease = usePoll(() => leaseApi.get(id), useFallback(LEASE_POLL_MS), [id])
  const app = usePoll(() => leaseApi.app(id), useFallback(LEASE_POLL_MS), [id])
  const l = lease.data
  const vm = l?.vm ?? null
  // VM 의 변경 알림은 VM id 를 싣는다 — 경로의 임대 id 로는 걸리지 않으므로 읽은 임대의 VM 으로 맞춘다.
  useRefetchOn(
    (c) => (c.kind === 'lease' && c.id === id) || (c.kind === 'vm' && vm !== null && c.id === vm.id),
    () => {
      lease.refresh()
      app.refresh()
    },
  )

  // 읽기 전에는 없다고 하지 않는다. 없는 임대(404)와 산 게스트가 없는 임대는 같은 `없는 VM` 이다.
  if (!l) {
    if (lease.error && !(lease.error instanceof ApiError && lease.error.status === 404)) {
      return (
        <>
          <Crumb steps={[{ label: 'VM', to: '/vms' }, { label: '' }]} />
          <Err error={lease.error} />
        </>
      )
    }
    return <Empty>{lease.loading ? '불러오는 중' : '없는 VM'}</Empty>
  }
  if (!vm) return <Empty>없는 VM</Empty>

  const node = world.nodes.find((r) => r.node.id === vm.node_id)
  const cluster = world.clusters.find((c) => c.id === vm.cluster_id)
  const acts = mayChange(l, me)
  // 태그는 소유자의 낱말이다(F-109) — 최고 관리자도 남의 VM 의 태그는 `임대` 줄에서 보기만 한다. 반납된 자기 VM 도 고친다.
  const tags = mayTag(l, me)
  const saved = () => {
    lease.refresh()
    refresh()
  }

  return (
    <>
      <Crumb steps={[{ label: 'VM', to: '/vms' }, { label: vm.name }]} />
      <PageHead
        icon={<MonitorSmartphone className="size-5" />}
        kind="VM"
        title={vm.name}
        right={
          <div className="mt-1 flex flex-col items-end gap-2">
            <div className="flex flex-wrap items-center justify-end gap-2">
              {tags ? <TagEditChip lease={l} onSaved={saved} /> : null}
              <ConsoleChip lease={l} className={CHIP} />
              <ToneBadge tone={vmToneOf(vm.state)}>{vmWord(vm.state)}</ToneBadge>
              {isUnreachable(vm) ? <ToneBadge tone="warn">{UNREACHABLE_WORD}</ToneBadge> : null}
            </div>
            {acts && !isClosed(l) ? (
              <PowerActions
                lease={l}
                onChanged={saved}
                endTo="/vms"
                extra={(busy) => (me.role === 'superadmin' ? <SpecChip lease={l} onSaved={saved} disabled={busy} /> : null)}
              />
            ) : null}
            {me.role === 'superadmin' ? <RetentionActions retained={retainedOf(l)} onChanged={lease.refresh} /> : null}
            {l.state === 'active' ? <ChangeLine lease={l} onChanged={lease.refresh} cancel={acts} /> : null}
          </div>
        }
        facts={
          <>
            사용자{' '}
            <Link to={`/users/${l.owner.id}`} className="font-medium text-foreground hover:underline">
              {ownerLabel(l.owner)}
            </Link>{' '}
            · {vmSpecText(vm)}
            {l.team ? <> · 팀 {l.team.name}</> : null}
          </>
        }
      />

      <Err error={lease.error} />

      <Section title="상태">
        <VmStatusPanel lease={l} />
      </Section>

      {l.owner.id === me.id || expert.on ? (
        <Section
          title="접속"
          right={
            <div className="flex items-center gap-1.5">
              {acts ? <KeyRotateChip lease={l} onChanged={lease.refresh} /> : null}
              <KeyDownload lease={l} />
            </div>
          }
        >
          <AccessPanel lease={l} />
          <GuestKeysLine lease={l} rotate={acts} />
        </Section>
      ) : null}

      <DiskPanel lease={l} admin onChanged={lease.refresh} />

      <Section title="앱">
        <Err error={app.error} />
        {app.data ? (
          // 서버 값이 바뀌면(다른 곳에서 저장·해제) key 가 바뀌어 입력이 새로 시작한다.
          <AppPanel key={app.data.port ?? ''} lease={l} app={app.data} onSaved={app.refresh} />
        ) : app.loading ? (
          <Empty>불러오는 중</Empty>
        ) : null}
      </Section>

      <Section title="임대">
        <div className="rounded-lg border bg-card px-4 py-3">
          <Facts
            rows={[
              ['상태', <ToneBadge key="s" tone={leaseToneOf(l.state)}>{leaseWord(l.state)}</ToneBadge>],
              ['태그', l.tags.length > 0 ? <VmTags key="t" tags={l.tags} /> : DASH],
              ...(l.purpose ? ([['요청 사유', l.purpose]] as [string, string][]) : []),
              ['요청', requestText(l)],
              ['신청', <Ago key="r" at={l.requested_at} />],
              ['시작', <Ago key="st" at={l.started_at} />],
              ...(l.purge_after ? ([['보존 기한', exact(l.purge_after)]] as [string, string][]) : []),
              ...(l.approved_by ? ([['승인', <>{l.approved_by.username} · <Ago at={l.approved_at} /></>]] as FactRow[]) : []),
              ...(l.end_reason ? ([['반납 사유', l.end_reason]] as [string, string][]) : []),
              ['접속 키', l.ssh_keys.length === 0 ? DASH : `${l.ssh_keys.length}개${l.ssh_key_generated ? ' · 생성 키' : ''}`],
            ]}
            expert={[
              ['VMID', String(vm.vmid)],
              ['노드', node ? <Link key="n" to={`/nodes/${node.node.id}`} className="hover:underline">{node.name}</Link> : DASH],
              ['클러스터', cluster ? <Link key="c" to="/clusters" className="hover:underline">{cluster.name}</Link> : DASH],
              ['풀', vm.pool ?? DASH],
              ['스토리지', vm.storage ?? DASH],
              ['관측', <>{observed(vm)} · {exact(vm.observed_at)}</>],
              ['신청 시각', exact(l.requested_at)],
              ['시작 시각', exact(l.started_at)],
              ...l.ssh_keys.map(
                (k, i): FactRow => [
                  `키 ${i + 1}`,
                  <span key={k.fingerprint} className="font-mono text-[11px]">
                    {k.fingerprint}
                    {k.generated ? ' · 생성 키' : ''}
                    {` · 주입 ${exact(k.injected_at)}`}
                  </span>,
                ],
              ),
            ]}
          />
        </div>
      </Section>

      <Section title="GPU">
        {vm.gpus.length === 0 ? (
          <Empty>GPU 없음</Empty>
        ) : (
          <div className="overflow-hidden rounded-lg border">
            {vm.gpus.map((g) => (
              <div key={g.gpu_id} className="flex items-center gap-3 border-b bg-card px-4 py-2.5 last:border-b-0">
                <CircuitBoard className="size-4 flex-none text-muted-foreground" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{g.model ?? DASH}</span>
                  <ExpertLine mono items={[g.pci_addr, `슬롯 ${g.hostpci_slot}`]} />
                </span>
                <span className="w-20 text-right text-[12px] text-muted-foreground">{g.vram_mb ? sizeMb(g.vram_mb) : ''}</span>
              </div>
            ))}
          </div>
        )}
      </Section>

    </>
  )
}

function observed(vm: Vm): ReactNode {
  if (!vm.observed_at) return DASH
  const s = vm.observed_status === 'running' ? '실행 중' : vm.observed_status === 'stopped' ? '정지' : DASH
  return (
    <>
      {s}
      {vm.observed_lock ? ' · 작업 중' : ''} · <Ago at={vm.observed_at} />
    </>
  )
}

// 최고 관리자의 사양 수정(B-43): 코어·메모리·GPU 를 승인 없이, 직위 한도에 묶이지 않고 정한다. 저장한 뒤 창이
// 지금 재부팅해 적용할지 묻는다. 열린 변경(승인 대기·적용 대기)이 있으면 잠긴다 — 임대당 하나이고, 기다리는 신청은
// 검토 대기에서 고쳐 승인한다.
function SpecChip({ lease: l, onSaved, disabled }: { lease: Lease; onSaved: () => void; disabled: boolean }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button
        type="button"
        className={`${ACTION} ${ACTION_PLAIN}`}
        onClick={() => setOpen(true)}
        disabled={disabled || l.state !== 'active' || !!l.pending_change}
      >
        사양 수정
      </button>
      <ChangeDialog
        lease={l}
        set
        open={open}
        onClose={() => setOpen(false)}
        onDone={() => {
          setOpen(false)
          onSaved()
        }}
      />
    </>
  )
}
